import { useState, useEffect } from 'react';
import { getProductByCode } from '../lib/requests.es6';
import { getPartner } from '../lib/access.es6';

const productCurrencyCache = {};
const pendingRequests = {};

export function fetchProductCurrency(productCode) {
  if (productCurrencyCache[productCode]) {
    return Promise.resolve(productCurrencyCache[productCode]);
  }

  if (!pendingRequests[productCode]) {
    pendingRequests[productCode] = getProductByCode(productCode)
      .then(response => {
        const currency = response && response.data && response.data.currency;
        if (currency) {
          productCurrencyCache[productCode] = currency;
        }
        delete pendingRequests[productCode];
        return currency;
      })
      .catch(function () {
        delete pendingRequests[productCode];
        return null;
      });
  }

  return pendingRequests[productCode];
}

// Resolves a product's currency by product_code, falling back to the
// partner's country currency when the product has none set or the
// lookup fails. Results are cached per product_code across the app so
// rendering many policies for the same product only fetches it once.
export default function useProductCurrency(productCode) {
  const fallbackCurrency = getPartner().country.currency_code;
  const [currency, setCurrency] = useState(
    (productCode && productCurrencyCache[productCode]) || null
  );

  useEffect(() => {
    if (!productCode || productCurrencyCache[productCode]) {
      return;
    }

    let isMounted = true;
    fetchProductCurrency(productCode).then(resolvedCurrency => {
      if (isMounted && resolvedCurrency) {
        setCurrency(resolvedCurrency);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [productCode]);

  return currency || fallbackCurrency;
}

// Resolves currencies for a list of product codes (e.g. a table of policies
// spanning multiple products). Returns a resolver function that maps a
// product_code to its currency, falling back to the partner's country
// currency when the product has none set or the lookup is still pending.
// Results share the same per-product_code cache as useProductCurrency.
export function useProductCurrencies(productCodes) {
  const fallbackCurrency = getPartner().country.currency_code;

  const codes = Array.isArray(productCodes)
    ? productCodes.filter(Boolean)
    : [];
  // Stable dependency key so the effect only re-runs when the set of
  // product codes actually changes (not on every render).
  const codesKey = codes.slice().sort().join(',');

  const [currencies, setCurrencies] = useState(function () {
    const initial = {};
    codes.forEach(function (code) {
      if (productCurrencyCache[code]) {
        initial[code] = productCurrencyCache[code];
      }
    });
    return initial;
  });

  useEffect(function () {
    let isMounted = true;
    const uniqueCodes = codes.filter(function (code, index) {
      return codes.indexOf(code) === index;
    });

    Promise.all(
      uniqueCodes.map(function (code) {
        return fetchProductCurrency(code).then(function (currency) {
          return { code: code, currency: currency };
        });
      })
    ).then(function (results) {
      if (!isMounted) {
        return;
      }
      setCurrencies(function (previous) {
        const next = { ...previous };
        let changed = false;
        results.forEach(function (result) {
          if (result.currency && next[result.code] !== result.currency) {
            next[result.code] = result.currency;
            changed = true;
          }
        });
        return changed ? next : previous;
      });
    });

    return function () {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codesKey]);

  return function (productCode) {
    return (productCode && currencies[productCode]) || fallbackCurrency;
  };
}
